import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"

export type RecoveryPool = {
  recoverypoolid: number
  propertyid: number
  code?: string
  name?: string
}

// private module logic //

const id: string = "recoveryPools"
const initialState: ApiResult<RecoveryPool[]> = { id, state: "init", value: [] }
const recoveryPoolsStream = new BehaviorSubject<ApiResult<RecoveryPool[]>>(initialState)
const recoveryPoolsEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let recoveryPoolsMap = {}
const _patches_ = PromiseWatch()
const _fetches_ = PromiseWatch()

function clear(): void {
  recoveryPoolsStream.next({ id, state: 'ok', value: [] })
  recoveryPoolsMap = {}
}

function get(recoverypoolid: number): RecoveryPool {
  return recoveryPoolsMap[recoverypoolid]
}

function refresh(): Promise<any> {
  const currentValue = recoveryPoolsStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchRecoveryPools(propertyid)
}

function fetchRecoveryPools(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<RecoveryPool[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  recoveryPoolsStream.next({ id, state: 'busy', value: recoveryPoolsStream.getValue().value })

  let url = `recoverypool?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `recoverypool?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: RecoveryPool[]) => {
    _.forEach(records, (recoveryPools) => (recoveryPoolsMap[recoveryPools.recoverypoolid] = recoveryPools))
    // merge results unique by recoverypoolid
    recoveryPoolsStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function fetchRecoveryPool(recoveryPoolId: number): Promise<RecoveryPool | { message: string }> {
  recoveryPoolsStream.next({ id, state: "busy", value: recoveryPoolsStream.getValue().value })

  return Client.get(`recoverypool?recoverypoolid=eq.${recoveryPoolId}`)
    .then((pools: RecoveryPool[]) => {
      _.forEach(pools, p => recoveryPoolsMap[p.recoverypoolid] = p)
      // merge results unique by recoverypoolid
      recoveryPoolsStream.next({ id, state: "ok", value: _.unionBy(pools, recoveryPoolsStream.getValue().value, "recoverypoolid") })
      return pools[0]
    })
    .catch((err: {message: string}) => {
      notificationService.notify({ type: "danger", message: err.message, dismiss: 5000 })
      return err
    })
}

function create(propertyid: number): Promise<RecoveryPool | { message: string }> {
  const currentValue = recoveryPoolsStream.getValue().value
  recoveryPoolsStream.next({ id, state: 'busy', value: currentValue })

  const payload = { propertyid }

  return Client.post(`recoverypool`, payload)
    .then((recoveryPools: RecoveryPool) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, recoveryPools.recoverypoolid),
        redofunc: () => create(propertyid)
      })
      recoveryPoolsMap[recoveryPools.recoverypoolid] = recoveryPools
      // merge results unique by recoverypoolid
      const currentValue = recoveryPoolsStream.getValue().value
      recoveryPoolsStream.next({
        id,
        state: 'ok',
        value: _.unionBy([recoveryPools], currentValue, 'recoverypoolid')
      })
      recoveryPoolsEventStream.next({ type: 'RECOVERYPOOL_CREATED', payload: recoveryPools })
      return recoveryPools
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryPoolsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchRecoveryPools(propertyid)
      return err
    })
}

function copy(propertyid:number, recoverypoolid:number, code:string) {
  const currentValue = recoveryPoolsStream.getValue().value;
  recoveryPoolsStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'recoverypool', recordid: recoverypoolid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((recoveryPools: RecoveryPool) => {
      const newRecord = recoveryPools[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.recoverypoolid),
        redofunc: () => copy(propertyid, recoverypoolid, code)
      });
      recoveryPoolsMap[newRecord.recoverypoolid] = newRecord
      // merge results unique by recoverypoolid
      const currentValue = recoveryPoolsStream.getValue().value
      recoveryPoolsStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'recoverypoolid').sort((a, b) => alphanum(a.code, b.code))
      })
      recoveryPoolsEventStream.next({ type: 'RECOVERYPOOL_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      recoveryPoolsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRecoveryPools(propertyid)
      return err
    })
}

function copies(propertyid: number, recoverypoolid: number, count: number) {
  const currentValue = recoveryPoolsStream.getValue().value;
  recoveryPoolsStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'recoverypool', recordid: recoverypoolid, _synchronous: true, unique: true, count};

  return Client.post(`msg/svc_recordcopy`, payload)
    .then((recoverypools: RecoveryPool[]) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, _.map(recoverypools, 'recoverypoolid')),
        redofunc: () => copies(propertyid, recoverypoolid, count)
      });
      _.forEach(recoverypools, newRecord => recoveryPoolsMap[newRecord.recoverypoolid] = newRecord)
      // merge results unique by recoverypoolid
      const currentValue = recoveryPoolsStream.getValue().value
      recoveryPoolsStream.next({
        id,
        state: 'ok',
        value: _.unionBy(recoverypools, currentValue, 'recoverypoolid').sort((a, b) => alphanum(a.code, b.code))
      })
      recoveryPoolsEventStream.next({ type: 'RECOVERYPOOL_COPIED', payload: recoverypools })
      return recoverypools
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      recoveryPoolsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchRecoveryPools(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  recoverypoolid: number
): Promise<boolean | { message: string }> {
  const currentValue = recoveryPoolsStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, recoverypoolid)
  const refreshFn = () => fetchRecoveryPools(propertyid)
  if (!(await handleReferences('recoverypool', recoverypoolid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted recoverypoolid
  const value = _(currentValue)
    .filter((p) => p.recoverypoolid !== recoverypoolid)
    .value()
  value.forEach((p) => (recoveryPoolsMap[p.recoverypoolid] = p))

  recoveryPoolsStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(recoverypoolid).then(() =>
  Client.del(`recoverypool?recoverypoolid=eq.${recoverypoolid}`)
    .then((recoveryPools: RecoveryPool) => {
      delete recoveryPoolsMap[recoveryPools.recoverypoolid]
      push_undo({
        undofunc: () => {
          Client.post('recoveryPools', recoveryPools)
          recoveryPoolsMap[recoveryPools.recoverypoolid] = recoveryPools
          recoveryPoolsStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, recoverypoolid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryPoolsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRecoveryPools(propertyid)
      return err
    })
  )
}

async function deleteMultiple(
  propertyid: number,
  recoverypoolids: number[]
): Promise<boolean | { message: string }> {
  const currentValue = recoveryPoolsStream.getValue().value

  const deleteFn = (ids) => deleteMultiple(propertyid, ids)
  const refreshFn = () => fetchRecoveryPools(propertyid)
  if (!(await handleReferences('recoverypool', recoverypoolids, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted recoverypoolid
  const value = _(currentValue)
    .filter((p) => !recoverypoolids.includes(p.recoverypoolid))
    .value()
  value.forEach((p) => (recoveryPoolsMap[p.recoverypoolid] = p))

  recoveryPoolsStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(recoverypoolids).then(() =>
  Client.del(`recoverypool?recoverypoolid=in.${recoverypoolids}`)
    .then((recoverypools: RecoveryPool[]) => {
      recoverypools.forEach(p => delete recoveryPoolsMap[p.recoverypoolid])
      push_undo({
        undofunc: () => {
          Client.post('recoveryPools', recoverypools)
          recoverypools.forEach(p => recoveryPoolsMap[p.recoverypoolid] = p)
          recoveryPoolsStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteMultiple(propertyid, recoverypoolids)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryPoolsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchRecoveryPools(propertyid)
      return err
    })
  )
}

function patch(recoveryPool: RecoveryPool): Promise<RecoveryPool | { message: string }> {
  const currentValue = recoveryPoolsStream.getValue().value
  const recoverypoolid: number = recoveryPool.recoverypoolid
  const currentrecoveryPools = _.find(currentValue, { recoverypoolid })
  push_undo({
    undofunc: () => patch(currentrecoveryPools),
    redofunc: () => patch(recoveryPool)
  })

  // optimistic update
  recoveryPoolsStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {recoverypoolid}), recoveryPool)
  })

  return _patches_.watch(recoverypoolid, Client.patch(`recoverypool?recoverypoolid=eq.${recoverypoolid}`, recoveryPool)
    .then((mf: RecoveryPool[]) => {
      _.forEach(mf, (recoveryPools) => (recoveryPoolsMap[recoveryPools.recoverypoolid] = recoveryPools))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      recoveryPoolsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentrecoveryPools, 'propertyid')
      if (propertyid) fetchRecoveryPools(propertyid)
      return err
    }))
}

// public module interface //

const recoveryPoolsService = {
  stream: () => recoveryPoolsStream.asObservable(),
  value: () => recoveryPoolsStream.getValue().value,
  eventStream: () => recoveryPoolsEventStream.asObservable(),
  clear,
  copy,
  copies,
  create,
  deleteOne,
  deleteMultiple,
  get,
  fetchRecoveryPools,
  fetchRecoveryPool,
  patch,
  refresh
}

export default recoveryPoolsService
